<template>
  <section id="skills" class="skills-section">
    <div class="section-header">
      <h2 class="section-title">{{ t('SkillsSection.title') }}</h2>
    </div>

    <div class="skills-container">
      <div class="hard-skills">
        <h3 class="category-title">{{ t('SkillsSection.tech_title') }}</h3>
        
        <div class="stack-grid">
          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_backend') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in backend" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>

          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_frontend') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in frontend" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>

          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_data') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in data" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>

          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_mobile') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in mobile" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>

          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_ia') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in ia" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>

          <div class="skill-card">
            <div class="card-header"><h4>{{ t('SkillsSection.cat_tools') }}</h4></div>
            <div class="tech-list">
              <div class="tech-item" v-for="tech in tools" :key="tech.name">
                <img :src="getIconUrl(tech.icon)" class="tech-icon" :alt="tech.name" />
                <span>{{ tech.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="soft-skills">
        <h3 class="category-title">{{ t('SkillsSection.soft_title') }}</h3>
        <div class="soft-grid">
          <div class="soft-badge" v-for="skill in softSkills" :key="skill">
            {{ t(`SkillsSection.${skill}`) }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const getIconUrl = (iconKey) => `https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/${iconKey}/${iconKey}-original.svg`

// Données Techniques (Les noms des technos restent en dur car ce sont des noms propres)
const backend = [ { name: 'Java', icon: 'java' }, { name: 'Python', icon: 'python' }, { name: 'Node.js', icon: 'nodejs' }, { name: 'PHP', icon: 'php' } ]
const frontend = [ { name: 'Vue.js', icon: 'vuejs' }, { name: 'JavaScript', icon: 'javascript' }, { name: 'HTML5', icon: 'html5' }, { name: 'CSS3', icon: 'css3' }, { name: 'Tailwind', icon: 'tailwindcss' } ]
const data = [ { name: 'PostgreSQL', icon: 'postgresql' }, { name: 'MySQL', icon: 'mysql' }, { name: 'MongoDB', icon: 'mongodb' }, { name: 'Redis', icon: 'redis' } ]
const mobile = [ { name: 'Swift', icon: 'swift' }, { name: 'Kotlin', icon: 'kotlin' } ]
const ia = [ { name: 'PyTorch', icon: 'pytorch' }, { name: 'Scikit-learn', icon: 'scikitlearn' } ]
const tools = [ { name: 'Git', icon: 'git' }, { name: 'Docker', icon: 'docker' }, { name: 'Linux', icon: 'linux' }, { name: 'VS Code', icon: 'vscode' }, { name: 'Bash', icon: 'bash' }, { name: 'OpenCV', icon: 'opencv' } ]

// Pour les soft skills, on stocke les CLÉS de traduction, pas le texte
const softSkills = [ "soft_analysis", "soft_solving", "soft_teamwork", "soft_autonomy", "soft_curiosity", "soft_time", "soft_adaptability", "soft_communication" ]
</script>

<style scoped>
.skills-section {
  max-width: 1000px;
  margin: 0 auto 100px;
  padding: 0 20px;
}

.section-header {
  margin-bottom: 40px;
  text-align: center; /* Titre centré plus classique */
}

.section-title {
  font-family: var(--font-tech, monospace);
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.category-title {
  font-family: var(--font-tech, monospace);
  font-size: 1.3rem;
  margin-bottom: 30px;
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
  color: #000;
  display: inline-block;
}

/* --- HARD SKILLS GRID --- */
.stack-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
}

.skill-card {
  background: #fff; /* Fond blanc pur pour contraste tech */
  border: 1px solid #000;
  border-radius: 4px;
  padding: 25px;
  transition: transform 0.3s, box-shadow 0.3s;
}

.skill-card:hover {
  transform: translateY(-5px);
  box-shadow: 8px 8px 0px rgba(0,0,0,0.15); /* Ombre "block" noire */
}

.card-header {
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.card-header h4 {
  margin: 0;
  font-family: var(--font-tech, monospace);
  font-size: 1rem;
  font-weight: bold;
  letter-spacing: 1px;
}

.tech-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tech-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  background: #f5f5f5;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  transition: all 0.2s;
}

.tech-item:hover {
  background: #000;
  color: #fff;
  border-color: #000;
}

/* Inversion de couleur pour l'icône au survol si nécessaire (optionnel) */
/* .tech-item:hover .tech-icon { filter: invert(1); } */

.tech-icon {
  width: 20px;
  height: 20px;
}

/* --- SOFT SKILLS GRID --- */
.soft-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.soft-badge {
  font-family: var(--font-main, sans-serif);
  background: #fff;
  border: 1px solid #000;
  color: #000;
  padding: 10px 20px;
  border-radius: 4px; /* Moins arrondi pour faire plus tech */
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s;
  cursor: default;
}

.soft-badge:hover {
  background: #000;
  color: #fff;
  transform: scale(1.05);
}

/* Responsive */
@media (max-width: 768px) {
  .stack-grid {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
}
</style>